import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import ApiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { setRows, addRow, removeRow, removeSingleRow, updateRow, clearRows } from '../../../Redux/actions/rowActions';
import { RxCross1 } from "react-icons/rx";
import AsyncSelect from 'react-select/async';

const DirectItems = ({ onRequisitionData, itemData, formData, currencyPrecision, isXtreamCompany }) => {
    const rows = useSelector(state => state.rows.rows);
    const dispatch = useDispatch();
    const [itemOptions, setItemOptions] = useState([]);
    const [itemRows, setItemRows] = useState([]);
    const [descriptionOptions, setDescriptionOptions] = useState([]);
    const apiService = new ApiService();
    const initialMount = useRef(true);
    const rowsUpdated = useRef(false);
    const [page, setPage] = useState(1);
    const [desPage, setDecPage] = useState(1);
    const [itemHasMore, setItemHasMore] = useState(true);
    const [descHasMore, setDescHasMore] = useState(true);
    const [itemLoader, setItemLoader] = useState(false);
    const [descLoader, setDescLoader] = useState(false);

    // Keep the latest options in refs so selection can immediately use
    // an item returned by the API, without waiting for React state to update.
    const itemOptionsRef = useRef([]);
    const descriptionOptionsRef = useRef([]);
    const defaultItemOptionsRef = useRef([]);
    const defaultDescriptionOptionsRef = useRef([]);

    useEffect(() => {
        const getItems = async () => {
            try {
                if (initialMount.current && itemData?.prDetails && itemData.prDetails.length > 0) {
                    const formattedRows = itemData?.prDetails?.map(detail => (
                        {
                            requisitionNo: detail.requisitionNo,
                            srno: detail.srno,
                            itemNo: detail.itemCode,
                            recordId: detail.itemCode,
                            itemDescription: detail.itemDescription,
                            branch: '',
                            qty: detail.qty,
                            uom: detail.uom,
                            baseQty: 0,
                            conValue: detail.baseQty,
                            baseAmount: detail.unitPrice,
                            specification: detail.specification,
                            amount: detail.amount,
                            stock: detail.stock,
                            bol: ''
                        }));
                    dispatch(setRows(formattedRows));
                    onRequisitionData(formattedRows);
                } else if (initialMount.current && itemData?.issueDetails && itemData.issueDetails.length > 0) {
                    const formattedRowsPromises = itemData?.issueDetails.map(async (detail, index) => {
                        const selecteditmstock = await apiService.getApiParamater('GET_ITEM_STORE', `?Warehouse=${itemData?.warehouse}&itemNo=${detail?.itemCode}`);
                        let item = selecteditmstock?.data.result?.[0]
                        return {
                            srno: detail.srno,
                            itemNo: detail.itemCode,
                            recordId: detail.itemCode,
                            itemDescription: detail.itemDescription,
                            branch: '',
                            qty: detail?.qty,
                            uom: item?.uom,
                            baseQty: detail?.qty * 1,
                            conValue: detail?.convToInvUOM,
                            unitPrice: item?.avgPrice,
                            specification: detail.specification,
                            amount: item?.avgPrice * detail?.qty,
                            stock: item?.stock,
                            bol: '',
                            cgsAcc: item.acCode,
                            cgsAccName: item.acName,
                        };
                    }).filter(row => row !== null);
                    const formattedRows = await Promise.all(formattedRowsPromises);
                    if (formattedRows.length > 0) {
                        dispatch(setRows(formattedRows));
                        onRequisitionData(formattedRows);
                    }
                    if (formData && formData && rows[0].itemNo && !rowsUpdated) {
                        updateRowsWithNewStock();
                        rowsUpdated.current = true;
                    }
                }
            } catch (error) {
                console.error('Error fetching items:', error);
            }
        };
        if (itemData) {
            getItems();
        }

    }, [itemData, dispatch, onRequisitionData, rows]);
    useEffect(() => {
        if (initialMount.current) {
            initialMount.current = false;
        } else {
            itemRows.forEach((row, index) => {
                dispatch(updateRow(index, row));
            });
        }
    }, [itemRows, dispatch]);
    useEffect(() => {
        if (formData && rows[0].itemNo) {
            updateRowsWithNewStock()
        }

    }, [formData]);
    // Load the complete item master once when the component mounts.
    // The full dataset stays in refs for fast frontend searching, while
    // only a small number of options are rendered by react-select.
    useEffect(() => {
        const loadItemMaster = async () => {
            if (itemLoader || descLoader) return;

            setItemLoader(true);
            setDescLoader(true);

            try {
                const params = new URLSearchParams();
                // params.append("PageNo", 1);
                // params.append("PageSize", 10000);

                const response = await apiService.getApiParamater(
                    "GET_ITEM",
                    `?${params.toString()}`
                );

                const allItems = response?.data?.result || [];

                // One API response is shared by both dropdowns.
                const itemNoOptions = allItems
                    .filter(item => item?.itemNo)
                    .map(item => ({
                        label: item.itemNo,
                        value: item,
                    }));

                const descriptionOptions = allItems
                    .filter(item => item?.itemDescription)
                    .map(item => ({
                        label: item.itemDescription,
                        value: item,
                    }));

                // Keep ALL records in refs for instant frontend search.
                itemOptionsRef.current = itemNoOptions;
                descriptionOptionsRef.current = descriptionOptions;

                // Render only the first 20 records initially.
                defaultItemOptionsRef.current = itemNoOptions.slice(0, 20);
                defaultDescriptionOptionsRef.current = descriptionOptions.slice(0, 20);

                setItemOptions(defaultItemOptionsRef.current);
                setDescriptionOptions(defaultDescriptionOptionsRef.current);

                // Pagination is no longer required because the complete
                // item master is already available on the frontend.
                setItemHasMore(false);
                setDescHasMore(false);
            } catch (error) {
                console.error("Error loading item master:", error);
                itemOptionsRef.current = [];
                descriptionOptionsRef.current = [];
                defaultItemOptionsRef.current = [];
                defaultDescriptionOptionsRef.current = [];
                setItemOptions([]);
                setDescriptionOptions([]);
            } finally {
                setItemLoader(false);
                setDescLoader(false);
            }
        };

        loadItemMaster();
    }, []);

    const [itemSearch, setItemSearch] = useState("");
    const [descSearch, setDescSearch] = useState("");

    // Fast frontend search. The complete item master is already cached in refs,
    // so typing never calls the API again.
    const searchLocalOptions = (options, search, limit = 20) => {
        const normalizedSearch = search.trim().toLowerCase();

        if (!normalizedSearch) {
            return options.slice(0, limit);
        }

        const words = normalizedSearch.split(/\s+/).filter(Boolean);

        const startsWithMatches = [];
        const containsMatches = [];

        options.forEach(option => {
            const label = String(option?.label || "").toLowerCase();

            // Every typed term must exist in the item text.
            const matches = words.every(word => label.includes(word));
            if (!matches) return;

            if (label.startsWith(normalizedSearch)) {
                startsWithMatches.push(option);
            } else {
                containsMatches.push(option);
            }
        });

        return [...startsWithMatches, ...containsMatches].slice(0, limit);
    };

    const loadOptions = (inputValue, callback) => {
        const search = inputValue?.trim() || "";
        setItemSearch(search);
        setPage(1);

        // Show a lightweight default list until the user has typed 2 characters.
        if (search.length < 2) {
            callback(defaultItemOptionsRef.current);
            return;
        }

        // 2+ characters: search the complete frontend cache instantly.
        callback(searchLocalOptions(itemOptionsRef.current, search, 20));
    };

    const loadDescOptions = (inputValue, callback) => {
        const search = inputValue?.trim() || "";
        setDescSearch(search);
        setDecPage(1);

        if (search.length < 2) {
            callback(defaultDescriptionOptionsRef.current);
            return;
        }

        // 2+ characters: search the complete frontend cache instantly.
        callback(searchLocalOptions(descriptionOptionsRef.current, search, 20));
    };

    // Kept for the existing dropdown wiring. There is no API pagination now
    // because all item records are already loaded on the frontend.
    const loadMoreOptions = async () => {};
    const loadMoreDescOptions = async () => {};

    const handleInputChange = async (index, fieldName, value) => {
        try {

            const isDuplicate = rows.some((row, rowIndex) => rowIndex !== index && (row.itemNo === value || row.itemDescription === value));
            if (isDuplicate) {
                toast.error('Please choose a different item.');
                return;
            }
            if (!formData) {
                toast.warning("Select From Location")
                return;
            }
            if (fieldName === 'qty') {
                const quantity = parseFloat(value);
                if (quantity < 0) {
                    toast.warning('Quantity cannot be less than 0.');
                    return;
                }
            }
            const updatedRows = [...rows];
            const updatedRow = { ...updatedRows[index], [fieldName]: value, srno: ("0" + (index + 1)).slice(-2) };
            if (fieldName === 'itemNo' || fieldName === 'itemDescription') {
                const selectedItm = fieldName === 'itemNo'
                    ? itemOptionsRef.current?.find(dep => dep?.label === value)
                    : descriptionOptionsRef.current?.find(dep => dep?.label === value);
                const queryParam = `itemNo=${selectedItm?.value?.itemNo}`
                const selecteditmstock = await apiService.getApiParamater('GET_ITEM_STORE', `?Warehouse=${formData}&${queryParam}`);
                let selectedItem = selecteditmstock?.data?.result?.[0]
                const getGLAccounts = await apiService.getApiParamater("GET_ITEM_GL", `?ItemNo=${selectedItem?.itemNo}`);
                let glAccount = getGLAccounts?.data?.result[0]
                if (selectedItem) {
                    updatedRow.itemDescription = selectedItem?.itemDescription;
                    updatedRow.requisitionNo = selectedItem?.itemDescription;
                    updatedRow.uom = selectedItem?.unitMeasurement;
                    updatedRow.itemNo = selectedItem?.itemNo;
                    updatedRow.specification = selectedItem?.specification;
                    updatedRow.baseQty = 0;
                    updatedRow.unitPrice = parseFloat(selecteditmstock?.data?.result?.[0]?.avgPrice?.toFixed(currencyPrecision || 0)) || 0;
                    updatedRow.stock = selecteditmstock?.data?.result?.[0]?.stock || 0;
                    updatedRow.cgsAcc = glAccount?.cgsAccountCode;
                    updatedRow.cgsAccName = glAccount?.cgsAccountName;
                    updatedRow.amount = 0;
                }

                // Keep the loaded options available for local searching.
            }
            if (fieldName === 'qty') {
                const quantity = parseFloat(value);
                const stock = updatedRow?.stock;
                const unitPrice = parseFloat(updatedRow?.unitPrice);

                if (!isXtreamCompany && quantity > stock) {
                    toast.warning('Enter a quantity less than or equal to the Stock.');
                    return
                } else {
                    const baseQty = 1;
                    updatedRow.baseQty = (!isNaN(quantity) && quantity !== 0) ? (quantity * baseQty).toFixed(currencyPrecision || 0) : 0;
                    updatedRow.amount = (!isNaN(quantity) && !isNaN(unitPrice)) ? (unitPrice * quantity).toFixed(currencyPrecision || 0) : 0;
                }
            }
            if (fieldName === 'baseAmount') {
                const quantity = parseFloat(updatedRow.qty);
                const stock = updatedRow.stock;
                const unitPrice = parseFloat(updatedRow.unitPrice);
                updatedRow.amount = (!isNaN(quantity) && !isNaN(unitPrice)) ? (unitPrice * quantity).toFixed(currencyPrecision || 0) : 0;
            }
            updatedRows[index] = updatedRow;
            dispatch(setRows(updatedRows));
            setItemRows(updatedRows);
            onRequisitionData(updatedRows);
        } catch (error) {
            console.error('Error handling input change:', error);
        }
    };
    const addNewRow = () => {
        const newRow = {
            srno: '',
            itemNo: '',
            requisitionNo: '',
            itemDescription: '',
            branch: '',
            qty: '',
            uom: '',
            baseQty: '',
            convRate: '',
            baseAmount: '',
            amount: '',
            stock: '',
            bol: '',
            recordId: '',
            specification: '',
            supplierId: '',
            cgsAcc: "",
            cgsAccName: "",
        };
        dispatch(addRow(newRow));
        const updatedRows = [...rows, newRow];
        setItemRows(updatedRows);
        onRequisitionData(updatedRows);
        dispatch(setRows(updatedRows)); // Ensure Redux state is updated here
    };
    const handleRemoveRow = (index) => {
        dispatch(removeSingleRow(index));
        const updatedRows = rows
            .filter((_, i) => i !== index)
            .map((row, i) => ({
                ...row,
                srno: i + 1
            }));
        onRequisitionData(updatedRows);
        setItemRows(updatedRows)
        dispatch(setRows(updatedRows));

    };
    const updateRowsWithNewStock = async () => {
        try {
            const updatedRows = rows.map(row => ({ ...row }));
            const apiPromises = updatedRows.map(async (row, index) => {
                const queryParam = `itemNo=${row.itemNo}`;

                const response = await apiService.getApiParamater(
                    'GET_ITEM_STORE',
                    `?Warehouse=${formData || itemData.warehouse}&${queryParam}`
                );
                if (response?.data?.statusCode === 200) {
                    const itemData = response.data.result[0];
                    updatedRows[index].stock = itemData.stock || 0;
                    updatedRows[index].itemDescription = itemData.itemDescription;
                    updatedRows[index].baseAmount = itemData.avgPrice || 0;
                } else if (response?.data?.statusCode === 204) {
                    updatedRows[index] = {
                        ...row,
                        stock: 0,
                    };
                }
            });
            await Promise.all(apiPromises);
            dispatch(setRows([...updatedRows]));
            onRequisitionData(updatedRows);
        } catch (error) {
            console.error('Error updating rows with new stock:', error);
        }
    }
    const preventNegativeSign = (e) => {
        if (e.key === "-") {
            e.preventDefault();
        }
    };
    return (
        <div className='table-container p-2 mb-5' style={{ overflowX: 'auto', height: "400px" }}>
            <table>
                <thead className='dynoTable'>
                    <tr>
                        <th></th>
                        <th>Sr no</th>
                        <th>Item No</th>
                        <th>Description</th>
                        <th>Item Specs</th>
                        <th>Inventory UOM</th>
                        <th>Qty</th>
                        <th>Inventory Qty</th>
                        {isXtreamCompany === true ? "" : <th>Stock</th>}
                        <th>Unit Price</th>
                        <th>Amount</th>
                        <th>CGS Acc </th>
                        <th>CGS Acc Name </th>
                    </tr>
                </thead>
                <tbody>
                    {Array.isArray(itemRows) && rows?.map((row, index) => (
                        <tr key={index}>
                            {itemData?.eye ?
                                <td className='m-0 p-0' style={{ border: "1px solid rgba(118, 118, 118, 0.3)", cursor: "not-allowed" }}>
                                    <div className='center'>
                                        <RxCross1 size={25} disabled />
                                    </div>
                                </td> :
                                <td className='m-0 p-0' style={{ border: "1px solid rgba(118, 118, 118, 0.3)" }}>
                                    <div onClick={() => handleRemoveRow(index)} className='center'>
                                        <RxCross1 size={25} />
                                    </div>
                                </td>
                            }
                            <td className='m-0 p-0'><input className='p-2' type="text" style={{ width: "80px" }} value={row.srno} disabled /></td>
                            <td className="m-0 p-0 itemCol">
                                {itemData?.eye ? (
                                    <input
                                        className="p-2"
                                        type="text"
                                        disabled
                                        value={row?.itemNo || ""}
                                    />
                                ) : (
                                    <AsyncSelect
                                        cacheOptions
                                        value={
                                            row?.itemNo
                                                ? {
                                                    value: row.itemNo,
                                                    label: row.itemNo,
                                                }
                                                : null
                                        }
                                        loadOptions={loadOptions}
                                        onChange={(selectedOption) =>
                                            handleInputChange(
                                                index,
                                                "itemNo",
                                                selectedOption?.label || ""
                                            )
                                        }
                                        className="descSelect"
                                        classNamePrefix="descSelect"
                                        onMenuScrollToBottom={loadMoreOptions}
                                        isLoading={itemLoader}
                                        noOptionsMessage={({ inputValue }) =>
                                            inputValue?.length < 2
                                                ? "Type at least 2 characters to search"
                                                : "No items found"
                                        }
                                    />
                                )}
                            </td>

                            <td className="m-0 p-0 descriptionCol">
                                {itemData?.eye ? (
                                    <input
                                        className="p-2"
                                        disabled
                                        value={row?.itemDescription || ""}
                                        style={{ width: "100%" }}
                                    />
                                ) : (
                                    <AsyncSelect
                                        cacheOptions
                                        value={
                                            row?.itemDescription
                                                ? {
                                                    value: row.itemDescription,
                                                    label: row.itemDescription,
                                                }
                                                : null
                                        }
                                        loadOptions={loadDescOptions}
                                        onChange={(selectedOption) =>
                                            handleInputChange(
                                                index,
                                                "itemDescription",
                                                selectedOption?.label || ""
                                            )
                                        }
                                        className="descSelect"
                                        classNamePrefix="descSelect"
                                        onMenuScrollToBottom={loadMoreDescOptions}
                                        isLoading={descLoader}
                                        noOptionsMessage={({ inputValue }) =>
                                            inputValue?.length < 2
                                                ? "Type at least 2 characters to search"
                                                : "No items found"
                                        }
                                    />
                                )}
                            </td>
                            {itemData?.eye ?
                                <td className='m-0 p-0'><input className='p-2' disabled value={row.specification} style={{ width: "130px" }} /></td>
                                :
                                <td className='m-0 p-0'><input className='p-2' style={{ width: "130px" }}
                                    type="text" value={row.specification} onChange={e => handleInputChange(index, 'specification', e.target.value)} /></td>
                            }
                            <td className='m-0 p-0'><input className='p-2' disabled type="text" value={row.uom} style={{ width: "130px" }} /></td>
                            {itemData?.eye ?
                                <td className='m-0 p-0'><input className='p-2' disabled value={row.qty} style={{ width: "100px" }} /></td>
                                :
                                <td className='m-0 p-0'><input className='p-2' type="number" value={row.qty} onKeyDown={preventNegativeSign}
                                    onWheel={(e) => e.target.blur()}
                                    style={{ width: "100px" }}
                                    onChange={e => handleInputChange(index, 'qty', e.target.value)} /></td>
                            }
                            <td className='m-0 p-0'><input className='p-2' disabled type="text" style={{ width: "130px" }} value={row.baseQty} /></td>
                            {isXtreamCompany === true ? "" :
                                <td className='m-0 p-0'><input className='p-2' disabled value={Number(row.stock).toFixed(2)} style={{ width: "130px" }} /></td>
                            }
                            <td className='m-0 p-0'><input className='p-2' disabled value={Number(row.unitPrice || 0).toFixed(currencyPrecision || 0)} style={{ width: "130px" }} /></td>
                            <td className='m-0 p-0'><input className='p-2' disabled value={Number(row.amount).toFixed(currencyPrecision || 0)} style={{ width: "120px" }} /></td>
                            <td className='m-0 p-0'><input className='p-2' disabled type="text" value={row.cgsAcc} style={{ width: "130px" }} /></td>
                            <td className='m-0 p-0'><input className='p-2' disabled type="text" value={row.cgsAccName} style={{ width: "130px" }} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {itemData?.eye ? "" :
                <div className='row mt-5'>
                    <div className='col-2'>
                        <button className='mainbtn ' onClick={addNewRow}>
                            Add Item
                        </button>
                    </div>
                </div>
            }
        </div>
    );
};

export default DirectItems;